// src/modules/Scheduling/shared/availability/zone-conversion.ts
/**
* Unavailable blocks are stored as wall clock in the user's primary region
* zone. People enter and read them in another zone: their device's, or the
* region the scheduling app is showing. These convert between the two.
*
* A weekly rule has no date, so the conversion uses this week's offsets. In
* weeks where the two zones change clocks on different dates the result moves
* by an hour; the backend accepts that drift.
*
* Plain Intl, no atalaya: node-environment tests reach this file.
*/
var WEEK = [
	"sunday",
	"monday",
	"tuesday",
	"wednesday",
	"thursday",
	"friday",
	"saturday"
];
var DAY_SHORT = {
	friday: "Fri",
	monday: "Mon",
	saturday: "Sat",
	sunday: "Sun",
	thursday: "Thu",
	tuesday: "Tue",
	wednesday: "Wed"
};
var HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
var formats = /* @__PURE__ */ new Map();
function wallClock(ms, timeZone) {
	let format = formats.get(timeZone);
	if (!format) {
		format = new Intl.DateTimeFormat("en-US", {
			day: "numeric",
			hour: "numeric",
			hourCycle: "h23",
			minute: "numeric",
			month: "numeric",
			timeZone,
			year: "numeric"
		});
		formats.set(timeZone, format);
	}
	const parts = format.formatToParts(new Date(ms));
	const get = (type) => Number(parts.find((part) => part.type === type)?.value);
	return {
		day: Date.UTC(get("year"), get("month") - 1, get("day")) / 864e5,
		minutes: get("hour") * 60 + get("minute")
	};
}
/** The instant a zone's clock reads `minutes` past midnight of `day`. */
function instantOf(day, minutes, timeZone) {
	const asUtc = day * 864e5 + minutes * 6e4;
	let ms = asUtc;
	for (let pass = 0; pass < 2; pass += 1) {
		const wall = wallClock(ms, timeZone);
		ms = asUtc - (wall.day * 864e5 + wall.minutes * 6e4 - ms);
	}
	return ms;
}
var weekdayOf = (day) => WEEK[((day + 4) % 7 + 7) % 7];
function minutesOf(hhmm) {
	const match = hhmm ? HHMM.exec(hhmm) : null;
	return match ? Number(match[1]) * 60 + Number(match[2]) : void 0;
}
var hhmm = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
/**
* Blocks written in `fromZone`, as they read in `toZone`, one for one. A block
* whose `end` is at or before its `start` runs overnight into the next day
* (`end == start` is a full 24 hours), on both sides, so a block that crosses
* midnight once converted stays one block. A 24-hour block starting at
* midnight reads as `allDay`. Malformed blocks are dropped.
*/
function convert(blocks, fromZone, toZone, now) {
	const today = wallClock(now, fromZone).day;
	const sunday = today - WEEK.indexOf(weekdayOf(today));
	return (blocks ?? []).flatMap((block) => {
		const weekday = WEEK.indexOf(block.day);
		const start = block.allDay ? 0 : minutesOf(block.start);
		const end = block.allDay ? 0 : minutesOf(block.end);
		if (weekday < 0 || start === void 0 || end === void 0) return [];
		if (fromZone === toZone) return [block];
		const day = sunday + weekday;
		const startMs = instantOf(day, start, fromZone);
		const endMs = instantOf(end <= start ? day + 1 : day, end, fromZone);
		const from = wallClock(startMs, toZone);
		const until = wallClock(endMs, toZone);
		const fullDay = block.allDay || start === end;
		if (fullDay && from.minutes === 0) return [{
			allDay: true,
			day: weekdayOf(from.day)
		}];
		return [{
			allDay: false,
			day: weekdayOf(from.day),
			end: hhmm(fullDay ? from.minutes : until.minutes),
			start: hhmm(from.minutes)
		}];
	});
}
/** What an editor sends, entered in `editorZone`, as the server stores it. */
function toStoredUnavailableTimes(blocks, editorZone, storedZone, now = Date.now()) {
	return convert(blocks, editorZone, storedZone, now);
}
/** Stored blocks as they read in `viewZone`. */
function toLocalUnavailableTimes(blocks, storedZone, viewZone, now = Date.now()) {
	return convert(blocks, storedZone, viewZone, now);
}
function clock12(minutes) {
	const hour = Math.floor(minutes / 60) % 24;
	return `${hour % 12 || 12}:${String(minutes % 60).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
}
/**
* A block's hours after its day: `9:00 AM - 5:00 PM`, naming the next day
* when it runs past midnight (`8:00 PM - Wed 3:00 AM`). Undefined for a whole
* day.
*/
function formatBlockHours(block) {
	const start = minutesOf(block.start);
	const end = minutesOf(block.end);
	if (block.allDay || start === void 0 || end === void 0) return void 0;
	const nextDay = DAY_SHORT[WEEK[(WEEK.indexOf(block.day) + 1) % 7]];
	return `${clock12(start)} - ${end <= start ? `${nextDay} ${clock12(end)}` : clock12(end)}`;
}
/** `Mon all day`, `Mon 9:00 AM - 5:00 PM`, `Tue 8:00 PM - Wed 3:00 AM`. */
function formatUnavailableBlock(block) {
	return `${DAY_SHORT[block.day]} ${formatBlockHours(block) ?? "all day"}`;
}
/** Several blocks in one line, comma separated. */
function formatUnavailableTimes(blocks) {
	return blocks.map(formatUnavailableBlock).join(", ");
}
/** Whether two zones read the same clock this week, so no conversion shows. */
function sameWallClock(a, b, now = Date.now()) {
	if (a === b) return true;
	const week = 6048e5;
	return [
		now,
		now + week / 2,
		now + week
	].every((ms) => {
		const x = wallClock(ms, a);
		const y = wallClock(ms, b);
		return x.day === y.day && x.minutes === y.minutes;
	});
}
export { DAY_SHORT, formatBlockHours, formatUnavailableBlock, formatUnavailableTimes, sameWallClock, toLocalUnavailableTimes, toStoredUnavailableTimes };

//# sourceMappingURL=zone-conversion.js.map